import { addDays, capacity } from "@founderdeck/core";
import { useNavigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useApp } from "../app/AppContext";
import type { Task } from "../lib/api";
import { hourLabel } from "../lib/date";
import { isHardLate, priColor } from "../lib/tasks";
import { TaskModal } from "./TaskModal";

/* ---------------- Toast ---------------- */
function Toast({
  toast,
  onClose,
}: {
  toast: { message: string; undo?: () => void } | null;
  onClose: () => void;
}) {
  if (!toast) return null;
  return (
    <div className="toast">
      <span>{toast.message}</span>
      {toast.undo && (
        <button
          onClick={() => {
            toast.undo?.();
            onClose();
          }}
        >
          Undo
        </button>
      )}
    </div>
  );
}

/* ---------------- Command palette ---------------- */
interface Command {
  group: string;
  icon: string;
  label: string;
  keys?: string;
  run: () => void;
}

function CommandPalette({ onClose }: { onClose: () => void }) {
  const app = useApp();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [idx, setIdx] = useState(0);

  const commands = useMemo<Command[]>(() => {
    const go = (to: string) => () => {
      navigate({ to });
      onClose();
    };
    const list: Command[] = [
      { group: "Go", icon: "→", label: "Plan", keys: "g p", run: go("/app/plan") },
      { group: "Go", icon: "→", label: "Board", keys: "g b", run: go("/app/board") },
      { group: "Go", icon: "→", label: "Tasks", keys: "g l", run: go("/app/tasks") },
      { group: "Go", icon: "→", label: "Matrix", keys: "g m", run: go("/app/matrix") },
      { group: "Go", icon: "→", label: "Stats", keys: "g s", run: go("/app/stats") },
      { group: "Ritual", icon: "☀", label: "Morning plan", run: () => { onClose(); app.openPlan(); } },
      { group: "Ritual", icon: "🌙", label: "Shutdown ritual", run: () => { onClose(); app.openShutdown(); } },
      { group: "Ritual", icon: "⚡", label: "Auto-place today's tasks", run: () => { onClose(); app.autoPlaceToday(); } },
      { group: "Ritual", icon: "↻", label: "Roll stale to today", run: () => { onClose(); app.rollStale(); } },
      { group: "Create", icon: "+", label: "New task", keys: "n", run: () => { onClose(); app.openTask(null); } },
      { group: "Timer", icon: "▶", label: "Start focus", run: () => { onClose(); app.openFocus(); } },
      { group: "Help", icon: "?", label: "Keyboard shortcuts", keys: "?", run: () => { onClose(); app.openHelp(); } },
    ];
    return list;
  }, [app, navigate, onClose]);

  const filtered = commands.filter(
    (c) =>
      c.label.toLowerCase().includes(q.toLowerCase()) ||
      c.group.toLowerCase().includes(q.toLowerCase()),
  );
  const active = Math.min(idx, Math.max(0, filtered.length - 1));

  let lastGroup = "";
  return (
    <div className="ovl" onMouseDown={onClose}>
      <div className="pal" onMouseDown={(e) => e.stopPropagation()}>
        <input
          autoFocus
          value={q}
          placeholder="Type a command…"
          onChange={(e) => {
            setQ(e.target.value);
            setIdx(0);
          }}
          onKeyDown={(e) => {
            if (e.key === "ArrowDown") {
              setIdx((i) => Math.min(filtered.length - 1, i + 1));
              e.preventDefault();
            } else if (e.key === "ArrowUp") {
              setIdx((i) => Math.max(0, i - 1));
              e.preventDefault();
            } else if (e.key === "Enter") {
              filtered[active]?.run();
            }
          }}
        />
        <div className="plist">
          {filtered.length === 0 && <div className="empty" style={{ padding: 12 }}>No commands.</div>}
          {filtered.map((c, i) => {
            const head = c.group !== lastGroup ? c.group : null;
            lastGroup = c.group;
            return (
              <div key={`${c.group}-${c.label}`}>
                {head && <div className="pgroup">{head}</div>}
                <div
                  className={`pitem${i === active ? " on" : ""}`}
                  onMouseEnter={() => setIdx(i)}
                  onClick={() => c.run()}
                >
                  <span className="pico">{c.icon}</span>
                  {c.label}
                  {c.keys && (
                    <span className="pk">
                      <kbd>{c.keys}</kbd>
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* ---------------- Help ---------------- */
const HELP: [string, string][] = [
  ["⌘/Ctrl K", "command palette"],
  ["g then p·b·l·m·s", "go to view"],
  ["n or /", "quick add"],
  ["f", "start focus timer"],
  ["?", "this help"],
  ["Esc", "close"],
];
function HelpModal({ onClose }: { onClose: () => void }) {
  return (
    <div className="ovl" onMouseDown={onClose}>
      <div className="help" onMouseDown={(e) => e.stopPropagation()}>
        <h3>Keyboard</h3>
        <div className="hgrid">
          {HELP.map(([k, l]) => (
            <div key={k} className="hitem">
              <span>{l}</span>
              <kbd>{k}</kbd>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ---------------- Morning plan ritual ---------------- */
function PlanModal({ onClose }: { onClose: () => void }) {
  const app = useApp();
  const [step, setStep] = useState(1);
  const [picked, setPicked] = useState<Set<string>>(
    () => new Set(app.tasks.filter((t) => t.doDate === app.today).map((t) => t.id)),
  );

  const candidates = app.tasks
    .filter((t) => t.status !== "done")
    .sort((a, b) => (isHardLate(b) ? 1 : 0) - (isHardLate(a) ? 1 : 0) || a.pri - b.pri);
  const selected = candidates.filter((t) => picked.has(t.id));
  const cap = capacity(selected.map((t) => ({ estMin: t.estMin, status: t.status })));

  function toggle(id: string) {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  return (
    <div className="ovl" onMouseDown={onClose}>
      <div className="modal" onMouseDown={(e) => e.stopPropagation()}>
        <div className="steps">
          {[1, 2, 3].map((i) => (
            <div key={i} className={`stepdot${i <= step ? " on" : ""}`} />
          ))}
        </div>

        {step === 1 && (
          <>
            <h3>☀ 1 · Pick today's tasks</h3>
            <div className="scroll">
              {candidates.map((t) => (
                <div
                  key={t.id}
                  className={`pickrow${picked.has(t.id) ? " on" : ""}`}
                  onClick={() => toggle(t.id)}
                >
                  <span className="pri" style={{ background: priColor(t.pri), color: priColor(t.pri), marginTop: 0 }} />
                  <span>
                    {t.title}
                    {t.energy === "high" ? " ⚡" : ""}
                  </span>
                  <span className="est" style={{ marginLeft: "auto" }}>
                    {t.projectId ? app.pmap.get(t.projectId)?.name : ""}
                  </span>
                </div>
              ))}
              {candidates.length === 0 && <div className="empty">No open tasks.</div>}
            </div>
            <div className="mact">
              <button className="mbtn ghost" onClick={onClose}>
                Cancel
              </button>
              <button className="mbtn" onClick={() => setStep(2)}>
                Next → estimates
              </button>
            </div>
          </>
        )}

        {step === 2 && (
          <>
            <h3>☀ 2 · Estimate honestly</h3>
            <div className="scroll">
              {selected.map((t) => (
                <div key={t.id} className="pickrow" style={{ cursor: "default" }}>
                  <span>{t.title}</span>
                  <input
                    className="estin"
                    type="number"
                    min={5}
                    step={5}
                    defaultValue={t.estMin ?? 30}
                    onChange={(e) =>
                      app.updateTask(t.id, { estMin: Math.max(5, Number(e.target.value) || 30) })
                    }
                  />
                  <span className="est">m</span>
                </div>
              ))}
            </div>
            <div className="caplab" style={{ marginTop: 8 }}>
              total <b>{Math.round(cap.usedMin / 6) / 10}h</b> of <b>{cap.capMin / 60}h</b>
              {cap.over ? " — over capacity, cut something" : ""}
            </div>
            <div className="mact">
              <button className="mbtn ghost" onClick={() => setStep(1)}>
                ← Back
              </button>
              <button className="mbtn" onClick={() => setStep(3)}>
                Next → timebox
              </button>
            </div>
          </>
        )}

        {step === 3 && (
          <>
            <h3>☀ 3 · Timebox it</h3>
            <p style={{ color: "var(--dim)", fontSize: 13, lineHeight: 1.7, marginBottom: 6 }}>
              Auto-place fills the grid: deep work lands in your 9–12 peak first, then the rest by
              priority. You can drag things around after.
            </p>
            <div className="mact">
              <button className="mbtn ghost" onClick={() => setStep(2)}>
                ← Back
              </button>
              <button
                className="mbtn ghost"
                onClick={() => {
                  app.planDay(picked, false);
                  onClose();
                }}
              >
                Just add, I'll drag
              </button>
              <button
                className="mbtn warm"
                onClick={() => {
                  app.planDay(picked, true);
                  onClose();
                }}
              >
                ⚡ Auto-place & finish
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/* ---------------- Shutdown ritual ---------------- */
function ShutdownModal({ onClose }: { onClose: () => void }) {
  const app = useApp();
  const clearedToday = app.tasks.filter(
    (t) => t.status === "done" && t.completedAt?.slice(0, 10) === app.today,
  ).length;
  const openToday = app.tasks.filter((t) => t.status !== "done" && t.doDate === app.today).length;
  const tomorrow = addDays(app.today, 1);
  const mits = app.tasks.filter((t) => t.mitDate === tomorrow && t.status !== "done").length;

  function rollTomorrow() {
    const tk = addDays(app.today, 1);
    for (const t of app.tasks) {
      if (t.status !== "done" && (t.doDate === app.today || (t.doDate && t.doDate < app.today))) {
        app.updateTask(t.id, { doDate: tk, fromHour: null });
      }
    }
    app.toast("Rolled unfinished → tomorrow.");
  }

  return (
    <div className="ovl" onMouseDown={onClose}>
      <div className="modal" onMouseDown={(e) => e.stopPropagation()}>
        <h3>🌙 Shutdown ritual</h3>
        <div style={{ color: "var(--dim)", fontSize: 13, lineHeight: 1.9 }}>
          <div>
            1. Cleared today: <b style={{ color: "var(--text)" }}>{clearedToday}</b> · still on
            today: <b style={{ color: "var(--text)" }}>{openToday}</b>
          </div>
          <div>
            2.{" "}
            {openToday > 0 ? (
              <button className="mini" onClick={rollTomorrow}>
                ↻ roll unfinished → tomorrow
              </button>
            ) : (
              "Board clean ✓"
            )}
          </div>
          <div>
            3. Tomorrow's Top 3 starred: <b style={{ color: "var(--text)" }}>{mits}/3</b>
            {mits < 3 ? " — star before closing" : " ✓"}
          </div>
        </div>
        <div className="mact">
          <button className="mbtn ghost" onClick={onClose}>
            Not yet
          </button>
          <button
            className="mbtn"
            onClick={() => {
              app.completeShutdown();
              onClose();
            }}
          >
            Shutdown complete
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Focus overlay ---------------- */
const RING = 2 * Math.PI * 56;
function FocusOverlay({ taskId, onClose }: { taskId: string | null; onClose: () => void }) {
  const app = useApp();
  const t: Task | undefined = app.tasks.find((x) => x.id === taskId);
  const { timer } = app;
  const total = (timer.mode === "work" ? timer.workMin : timer.brkMin) * 60;
  const mm = String(Math.floor(timer.left / 60)).padStart(2, "0");
  const ss = String(timer.left % 60).padStart(2, "0");

  return (
    <div className="focus">
      <button className="iconbtn fexit" onClick={onClose}>
        ✕ exit
      </button>
      <div className="fp">
        {t?.projectId ? app.pmap.get(t.projectId)?.name : "Focus"}
        {t?.energy === "high" ? " · ⚡ deep work" : ""}
      </div>
      <div className="ft">{t?.title ?? "Focus"}</div>
      <div className="tring">
        <svg width="130" height="130">
          <circle cx="65" cy="65" r="56" fill="none" stroke="var(--line)" strokeWidth="6" />
          <circle
            cx="65"
            cy="65"
            r="56"
            fill="none"
            stroke={timer.mode === "work" ? "var(--accent)" : "var(--ok)"}
            strokeWidth="6"
            strokeLinecap="round"
            strokeDasharray={RING}
            strokeDashoffset={RING * (1 - timer.left / total)}
          />
        </svg>
        <div className="tv">
          {mm}:{ss}
        </div>
      </div>
      <div className="fp">{timer.running ? (timer.mode === "work" ? "focused" : "break") : "paused"}</div>
      <div className="presets">
        {[
          [25, 5],
          [52, 17],
          [90, 20],
        ].map(([w, b]) => (
          <button
            key={w}
            className={`preset${timer.workMin === w ? " on" : ""}`}
            onClick={() => timer.setPreset(w!, b!)}
          >
            {w}/{b}
          </button>
        ))}
      </div>
      <div className="fctl">
        <button className="mbtn" onClick={() => timer.toggle()}>
          {timer.running ? "Pause" : "Start"}
        </button>
        <button
          className="mbtn ghost"
          onClick={() => {
            if (t) app.toggleDone(t);
            if (timer.running) timer.toggle();
            onClose();
          }}
        >
          ✓ Complete task
        </button>
      </div>
    </div>
  );
}

/* ---------------- Aggregator ---------------- */
export function Overlays(props: {
  toast: { message: string; undo?: () => void } | null;
  onToastClose: () => void;
  palette: boolean;
  onPaletteClose: () => void;
  help: boolean;
  onHelpClose: () => void;
  plan: boolean;
  onPlanClose: () => void;
  shutdown: boolean;
  onShutdownClose: () => void;
  focusOpen: boolean;
  focusId: string | null;
  onFocusClose: () => void;
  taskModal: { id: string | null } | null;
  onTaskClose: () => void;
}) {
  return (
    <>
      <Toast toast={props.toast} onClose={props.onToastClose} />
      {props.palette && <CommandPalette onClose={props.onPaletteClose} />}
      {props.help && <HelpModal onClose={props.onHelpClose} />}
      {props.plan && <PlanModal onClose={props.onPlanClose} />}
      {props.shutdown && <ShutdownModal onClose={props.onShutdownClose} />}
      {props.focusOpen && <FocusOverlay taskId={props.focusId} onClose={props.onFocusClose} />}
      {props.taskModal && <TaskModal taskId={props.taskModal.id} onClose={props.onTaskClose} />}
    </>
  );
}
